import {
  buildResultTemplatesManager,
  FoldedCollection,
  ResultTemplatesHelpers,
  ResultTemplatesManager,
} from '@coveo/headless';
import { useCoveoSearchEngine } from '../../CoveoSearchEngine';
import React, { useRef } from 'react';
import { PageResults } from './PageResults';
import { IADocsSourceName } from 'Feature/Coveo/Helpers/AppHelper';
import { ProductGuideResults } from './ProductGuideResults';
import { SitecoreProductGuideResults } from './SitecoreProductGuideResults';
import { NewsArticleResults } from './NewsArticleResults';

type ResultTemplatesContextType = {
  templateManager: ResultTemplatesManager<unknown>;
};
const ResultTemplatesContext = React.createContext<any | undefined>(undefined);
ResultTemplatesContext.displayName = 'ResultTemplatesContext';

export function ResultTemplatesProvider(props: any) {
  const { headlessEngine } = useCoveoSearchEngine();
  const resultTemplateManager = useRef(buildResultTemplatesManager(headlessEngine!));
  resultTemplateManager.current.registerTemplates(
    {
      conditions: [ResultTemplatesHelpers.fieldMustMatch('source', [IADocsSourceName])],
      priority: 10,
      content: (collection: FoldedCollection) => (
        <React.Fragment key={collection.result.uniqueId}>
          <ProductGuideResults collection={collection} />
        </React.Fragment>
      ),
    },
    {
      conditions: [ResultTemplatesHelpers.fieldMustMatch('lmccontenttype', ['Product Guide'])],
      priority: 9,
      content: (collection: FoldedCollection) => (
        <React.Fragment key={collection.result.uniqueId}>
          <SitecoreProductGuideResults collection={collection} />
        </React.Fragment>
      ),
    },
    {
      conditions: [ResultTemplatesHelpers.fieldMustMatch('lmccontenttype', ['Document'])],
      priority: 11,
      content: (collection: FoldedCollection) => (
        <React.Fragment key={collection.result.uniqueId}>
          <PageResults collection={collection} />
        </React.Fragment>
      ),
    },
    {
      conditions: [ResultTemplatesHelpers.fieldMustMatch('lmccontenttype', ['News Article'])],
      priority: 12,
      content: (collection: FoldedCollection) => (
        <React.Fragment key={collection.result.uniqueId}>
          <NewsArticleResults collection={collection} />
        </React.Fragment>
      ),
    },
    {
      conditions: [],
      priority: 1,
      content: (collection: FoldedCollection) => (
        <React.Fragment key={collection.result.uniqueId}>
          <PageResults collection={collection} />
        </React.Fragment>
      ),
    },
  );
  const value: ResultTemplatesContextType = React.useMemo(() => {
    return { templateManager: resultTemplateManager.current };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <>
      <ResultTemplatesContext.Provider value={value} {...props} />
    </>
  );
}

export function useResultTemplates(): ResultTemplatesContextType {
  const context = React.useContext(ResultTemplatesContext);
  if (context === undefined) {
    throw new Error(`useResultTemplates must be used within a ResultTemplatesProvider`);
  }
  return context;
}
